import React, { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import IconContainer from "@/components/ui/icon-container";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import SingleInput from "@/components/controls/SingleInput";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import {
  ChevronRight,
  Close,
  Contracts as ContractsIcon
} from "@dreamhub/designsystem/icons";
import { useProducts } from "@/entities/settings/hooks";
import { ComputedFields } from "@/services/api/types";
import { ContractData } from "@/pages/contracts/types";
import {
  ContractUpdatePayload,
  useContract,
  useDuplicateContract,
  useUpdateContract
} from "@/pages/contracts/hooks";
import { computeContractDateRange } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/utils";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import SourceContractBanner from "./SourceContractBanner";
import DuplicateServicesSection from "./DuplicateServicesSection";
import DuplicateTopFields from "./DuplicateTopFields";
import CarryOverSection from "./CarryOverSection";
import { useDuplicateContractDraft } from "./useDuplicateContractDraft";
import { estimateFinancials, financialDeltas } from "./financials";
import { getDraftRowStatus } from "@/pages/contracts/components/termsDraft/types";
import { DEFAULT_CARRY_OVER, DuplicateContractFields } from "./types";

// The app shell insets its content by mt-14 + mb-6 (80px) but --app-content-h
// only subtracts the 64px nav, so a page that fills it overflows the window by
// the 16px remainder. Subtract it so the body scroller is the only scrollbar.
const PAGE_HEIGHT_CLASS =
  "flex flex-col h-full max-h-[calc(var(--app-content-h)-16px)]";

const DuplicateContractPage: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const {
    data: sourceContract,
    isLoading,
    isError
  } = useContract(contractId, { computed_fields: ComputedFields.ALL });

  if (isError) {
    return <NotFoundPage data-test="duplicate-contract-404" />;
  }

  if (isLoading || !sourceContract) {
    return (
      <div className="flex items-center justify-center h-full w-full min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return <DuplicateContractContent sourceContract={sourceContract} />;
};

const DuplicateContractContent: React.FC<{ sourceContract: ContractData }> = ({
  sourceContract
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const duplicateContract = useDuplicateContract();
  const updateContract = useUpdateContract(sourceContract.id);
  const queryClient = useQueryClient();
  const draft = useDuplicateContractDraft(sourceContract);
  const products = useProducts().data?.products ?? [];

  const defaultName = `${sourceContract.name} ${t(
    "contracts.duplicate.nameCopySuffix"
  )}`;
  const [name, setName] = useState(defaultName);
  const [carryOver, setCarryOver] = useState(DEFAULT_CARRY_OVER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOverlapConfirm, setShowOverlapConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);
  const [isProductConfirmChecked, setIsProductConfirmChecked] = useState(false);

  const initialFields: DuplicateContractFields = useMemo(
    () => ({
      companyId: sourceContract.companyId,
      productId: sourceContract.productId,
      csmOwner: sourceContract.csmOwner
    }),
    [sourceContract]
  );
  const [fields, setFields] = useState(initialFields);
  const productId = fields.productId;

  const productName = (id: string | null) =>
    products.find((product) => String(product.id) === String(id))?.name;

  const dateRange = useMemo(
    () =>
      computeContractDateRange(
        draft.items.filter((item) => !item.draftDeleted),
        sourceContract.startDate,
        sourceContract.endDate
      ),
    [draft.items, sourceContract.startDate, sourceContract.endDate]
  );

  const newValues = useMemo(() => {
    const deltas = financialDeltas(
      estimateFinancials(draft.items),
      estimateFinancials(sourceContract.revenueItems)
    );
    return {
      tcv: (sourceContract.calculatedTotalContractValueLocal ?? 0) + deltas.tcv,
      cmrr:
        (sourceContract.calculatedCmrrLocal ??
          sourceContract.calculatedMrrLocal ??
          0) + deltas.cmrr
    };
  }, [draft.items, sourceContract]);

  const hasChanges = useMemo(
    () =>
      name !== defaultName ||
      JSON.stringify(fields) !== JSON.stringify(initialFields) ||
      JSON.stringify(carryOver) !== JSON.stringify(DEFAULT_CARRY_OVER) ||
      draft.items.length !== sourceContract.revenueItems.length ||
      draft.items.some(
        (item) => getDraftRowStatus(item, draft.baseline) !== null
      ),
    [name, defaultName, fields, initialFields, carryOver, draft, sourceContract]
  );

  // The duplicate endpoint only accepts productId, so a company or owner edit
  // is applied to the created contract in a follow-up update.
  const patchEditedFields = async (newContractId: string) => {
    const patch: ContractUpdatePayload = {};
    if (fields.companyId !== initialFields.companyId) {
      patch.companyId = fields.companyId ?? undefined;
    }
    if (fields.csmOwner !== initialFields.csmOwner) {
      patch.csmOwner = fields.csmOwner;
    }
    if (Object.keys(patch).length === 0) return;
    await updateContract.mutateAsync({
      pathParams: { contractId: newContractId },
      data: patch
    });
    // useUpdateContract only invalidates the source contract it was created
    // with, and the page navigates straight to the new one.
    await queryClient.invalidateQueries({
      queryKey: ["contracts/id", { pathParams: { contractId: newContractId } }]
    });
  };

  const handleProductChange = (nextProductId: string) => {
    if (nextProductId === fields.productId) return;
    if (draft.items.length === 0) {
      setFields((current) => ({ ...current, productId: nextProductId }));
      return;
    }
    setIsProductConfirmChecked(false);
    setPendingProductId(nextProductId);
  };

  const confirmProductChange = () => {
    if (!pendingProductId) return;
    draft.replaceItems([]);
    draft.setSelected([]);
    setFields((current) => ({ ...current, productId: pendingProductId }));
    setPendingProductId(null);
    setIsProductConfirmChecked(false);
  };

  const closePage = () => navigate(`/contracts/${sourceContract.id}`);

  const handleCloseAttempt = () => {
    if (hasChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    closePage();
  };

  const handleSubmit = async (acknowledgeOverlap: boolean) => {
    setIsSubmitting(true);
    try {
      const response = await duplicateContract.mutateAsync({
        pathParams: { contractId: sourceContract.id },
        data: {
          name: name.trim(),
          productId,
          revenueItems: draft.toPayloadItems(),
          carryOver,
          acknowledgeOverlap
        }
      });
      await patchEditedFields(response.id);
      navigate(`/contracts/${sourceContract.id}`);
      toast.success(
        <ToastBody
          title={t("contracts.duplicate.success")}
          actions={
            <>
              <Button
                variant="secondaryNoBg"
                className="text-primary"
                onClick={() => {
                  toast.dismiss();
                  navigate(`/contracts/${response.id}`);
                }}
              >
                {t("contracts.duplicate.viewNewContract")}
              </Button>
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            </>
          }
        />,
        { icon: null }
      );
    } catch (error) {
      setIsSubmitting(false);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setShowOverlapConfirm(true);
        return;
      }
      toast.error(
        <ToastBody
          title={t("contracts.duplicate.error")}
          actions={
            <IconButton
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
              onClick={() => toast.dismiss()}
            >
              <Close />
            </IconButton>
          }
        />,
        { icon: null }
      );
    }
  };

  const dateRangeLabel = dateRange
    ? `${formatDateShort(dateRange.start)} - ${formatDateShort(dateRange.end)}`
    : null;

  return (
    <div className={PAGE_HEIGHT_CLASS} data-test="duplicate-contract-page">
      <SourceContractBanner
        sourceContractId={sourceContract.id}
        sourceContractName={sourceContract.name}
      />
      <div className="flex flex-col flex-1 min-h-0 m-2 rounded-xl border-[1.5px] border-dashed border-border-border2">
        <div
          className="flex flex-col shrink-0 w-full"
          data-test="duplicate-contract-header"
        >
          <div className="w-full flex justify-between p-4 pb-2">
            <div className="flex gap-1 items-center">
              <div className="flex gap-[10px] items-center">
                <LogoIconContainer
                  icon={<ContractsIcon />}
                  index={-1}
                  className="size-8 flex-shrink-0"
                  iconSize="5"
                />
                <Link to="/contracts" data-test="duplicate-breadcrumb-link">
                  <Text variant="lgSubtitleH3" className="p-1">
                    {t("pages.contracts")}
                  </Text>
                </Link>
              </div>
              <IconContainer color="secondary" size="5">
                <ChevronRight />
              </IconContainer>
              <SingleInput
                fieldName="name"
                initialValue={name}
                onSave={(newValue: string) => setName(newValue)}
                textVariant="lgSubtitleH1"
                inputVariant="lgSubtitleH1"
                className="pl-0"
                width="fit-content"
                placeholder={t("contracts.detailedView.emptyName")}
                divClassName="py-0 px-2"
              />
            </div>
            <IconButton
              variant="iconNoBg"
              size="lg"
              iconColor="primary"
              onClick={handleCloseAttempt}
              data-test="duplicate-contract-close"
            >
              <Close />
            </IconButton>
          </div>
          <div data-test="duplicate-contract-info-chips">
            <DuplicateTopFields
              sourceContractId={sourceContract.id}
              fields={fields}
              currency={sourceContract.currency}
              dateRangeLabel={dateRangeLabel}
              tcv={newValues.tcv}
              cmrr={newValues.cmrr}
              onFieldChange={(patch) =>
                setFields((current) => ({ ...current, ...patch }))
              }
              onProductChange={handleProductChange}
            />
          </div>
        </div>
        <div
          className="flex flex-col flex-1 min-h-0 overflow-y-auto"
          data-test="duplicate-contract-body"
        >
          <div className="flex flex-col gap-8 p-4 pt-0">
            <DuplicateServicesSection
              draft={draft}
              sourceContract={sourceContract}
              productId={productId}
            />
            <CarryOverSection
              options={carryOver}
              onOptionsChange={setCarryOver}
            />
          </div>
        </div>
        <div
          className="flex items-center justify-between shrink-0 min-h-12 mx-4 mb-4 p-[10px] bg-surface-elevation2 rounded-2xl border-[0.5px] border-border-border1 shadow-popUp"
          data-test="duplicate-contract-footer"
        >
          <Button
            variant="secondary"
            onClick={handleCloseAttempt}
            data-test="duplicate-contract-cancel-button"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || isSubmitting}
            onClick={() => handleSubmit(false)}
            data-test="duplicate-contract-submit-button"
          >
            {t("contracts.duplicate.createContract")}
          </Button>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showOverlapConfirm}
        setIsOpen={setShowOverlapConfirm}
        title={t("contracts.duplicate.overlap.title")}
        confirmationText={t("contracts.duplicate.overlap.message")}
        submitButtonText={t("contracts.duplicate.overlap.create")}
        cancelButtonText={t("contracts.duplicate.overlap.continue")}
        isPrimaryButton
        handleSubmit={() => {
          setShowOverlapConfirm(false);
          handleSubmit(true);
        }}
        handleCancel={() => setShowOverlapConfirm(false)}
        data-test="duplicate-overlap-confirmation"
        hideDivider
      />
      <ConfirmationModal
        isOpen={!!pendingProductId}
        setIsOpen={() => setPendingProductId(null)}
        title={t("deals.dealView.changeProductModal.title", {
          newProductName: productName(pendingProductId) ?? ""
        })}
        confirmationText={t("deals.dealView.changeProductModal.description", {
          currentProductName: productName(fields.productId) ?? "",
          newProductName: productName(pendingProductId) ?? ""
        })}
        submitButtonText={t("deals.dealView.changeProductModal.submitButton")}
        cancelButtonText={t("cancel")}
        isPrimaryButton={false}
        checkboxLabel={t("deals.dealView.changeProductModal.checkboxLabel")}
        isCheckboxRequired
        isCheckboxChecked={isProductConfirmChecked}
        onCheckboxChange={setIsProductConfirmChecked}
        handleSubmit={confirmProductChange}
        handleCancel={() => {
          setPendingProductId(null);
          setIsProductConfirmChecked(false);
        }}
        data-test="duplicate-change-product-confirmation"
      />
      <UnsavedChangesWarningDialog
        isOpen={showDiscardConfirm}
        title={t("contracts.duplicate.discard.title")}
        message={t("contracts.duplicate.discard.message")}
        discardButtonText={t("contracts.duplicate.discard.discard")}
        cancelButtonText={t("contracts.duplicate.discard.continue")}
        onDiscard={() => {
          setShowDiscardConfirm(false);
          closePage();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  );
};

export default DuplicateContractPage;
